<template>
  <div class="max-w-4xl mx-auto">
    <!-- Competition Header -->
    <CompetitionHeader
      v-if="competition"
      :competition="competition"
      :participant-count="0"
      :show-delete-button="false"
    />

    <!-- Join Form -->
    <div class="card bg-base-100 card-bordered">
      <div class="card-body">
        <h3 class="card-title text-xl mb-6">Join Competition</h3>
        
        <form @submit.prevent="handleSubmit" class="space-y-6">
          <!-- User Info - Only show for unauthenticated users -->
          <div v-if="!authStore.isAuthenticated" class="grid md:grid-cols-2 gap-4">
            <div class="form-control">
              <label class="label">
                <span class="label-text">Full Name *</span>
              </label>
              <input 
                v-model="form.fullName"
                type="text" 
                placeholder="Enter your full name"
                class="input input-bordered" 
                required
              />
              <label class="label">
                <span class="label-text-alt">
                  Required for competition approval (visible to creator only)
                </span>
              </label>
            </div>
            
            <div class="form-control">
              <label class="label">
                <span class="label-text">Email *</span>
              </label>
              <input 
                v-model="form.email"
                type="email" 
                placeholder="Enter your email"
                class="input input-bordered" 
                required
              />
              <label class="label">
                <span class="label-text-alt">
                  Required for approval notifications
                </span>
              </label>
            </div>
          </div>

          <!-- Display Name -->
          <div class="form-control">
            <label class="label">
              <span class="label-text">Display Name</span>
            </label>
            <input 
              v-model="form.displayName"
              type="text" 
              placeholder="Enter a display name for the competition"
              class="input input-bordered" 
              required
            />
            <label class="label">
              <span class="label-text-alt">
                This name will be shown on the leaderboard. You can use your full name or create a unique display name.
              </span>
            </label>
            <div class="mt-2">
              <button 
                type="button"
                @click="generateDisplayName"
                class="btn btn-outline btn-sm"
              >
                Generate Display Name
              </button>
              <button 
                type="button"
                @click="useFullName"
                class="btn btn-outline btn-sm ml-2"
              >
                Use Full Name
              </button>
            </div>
          </div>

          <!-- Stock Picks -->
          <div class="form-control">
            <label class="label">
              <span class="label-text">Stock Picks ({{ competition?.required_stock_count || 0 }} required)</span>
            </label>
            
            <!-- Stock Search Component -->
            <StockSearch
              v-model="selectedStocks"
              :weights="stockWeights"
              :allow-weights="competition?.allow_weights"
              :max-stocks="competition?.required_stock_count"
              :disabled="loading"
              @stock-selected="handleStockSelected"
              @stock-removed="handleStockRemoved"
              @update:weights="handleWeightsUpdate"
            />
          </div>

          <!-- Submit Button -->
          <div class="form-control mt-6">
            <button 
              type="submit" 
              class="btn btn-primary"
              :disabled="loading || !isFormValid"
            >
              <span v-if="loading" class="loading loading-spinner loading-sm"></span>
              {{ loading ? 'Joining...' : 'Join Competition' }}
            </button>
          </div>
        </form>

        <!-- Error Display -->
        <div v-if="error" class="alert alert-error mt-4">
          <i class="mdi mdi-alert-circle h-6 w-6"></i>
          <span>{{ error }}</span>
        </div>

        <!-- Success Display -->
        <div v-if="success" class="alert alert-success mt-4">
          <i class="mdi mdi-check-circle h-6 w-6"></i>
          <div>
            <h3 class="font-bold">Successfully Joined!</h3>
            <div class="text-xs">
              <span v-if="joinResponse?.status === 'pending_approval'">
                Your join request has been submitted and is pending approval by the competition creator.
                You'll be notified once your request is reviewed.
              </span>
              <span v-else>
                Redirecting to competition details...
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useCompetitionStore } from '../stores/competition'
import { useAuthStore } from '../stores/auth'
import StockSearch from '../components/StockSearch.vue'
import CompetitionHeader from '../components/CompetitionHeader.vue'

const route = useRoute()
const router = useRouter()
const competitionStore = useCompetitionStore()
const authStore = useAuthStore()

const competitionId = route.query.id
const competition = ref(null)
const loading = ref(false)
const error = ref('')
const success = ref(false)
const joinResponse = ref(null)

const form = reactive({
  fullName: '',
  email: '',
  displayName: ''
})

const selectedStocks = ref([])
const stockWeights = ref({})

const totalWeight = computed(() => {
  if (!competition.value?.allow_weights) return 0
  return Object.values(stockWeights.value).reduce((sum, weight) => {
    const numWeight = parseInt(weight) || 0
    return sum + numWeight
  }, 0)
})

const isFormValid = computed(() => {
  if (!competition.value) return false
  
  // For authenticated users, only check display name and stocks
  if (authStore.isAuthenticated) {
    const hasDisplayName = form.displayName.trim()
    const hasRequiredStocks = selectedStocks.value.length === competition.value.required_stock_count
    const allStocksSelected = selectedStocks.value.length > 0
    
    if (competition.value.allow_weights) {
      const weightsValid = totalWeight.value === 100
      return hasDisplayName && hasRequiredStocks && allStocksSelected && weightsValid
    }
    
    return hasDisplayName && hasRequiredStocks && allStocksSelected
  }
  
  // For unauthenticated users, check all fields
  const hasRequiredFields = form.fullName.trim() && form.email.trim() && form.displayName.trim()
  const hasRequiredStocks = selectedStocks.value.length === competition.value.required_stock_count
  const allStocksSelected = selectedStocks.value.length > 0
  
  if (competition.value.allow_weights) {
    const weightsValid = totalWeight.value === 100
    return hasRequiredFields && hasRequiredStocks && allStocksSelected && weightsValid
  }
  
  return hasRequiredFields && hasRequiredStocks && allStocksSelected
})

const handleStockSelected = (stock) => {
  // Initialize weight to 0 for new stock
  if (competition.value?.allow_weights) {
    // Calculate smart distribution for all selected stocks (including the new one)
    const totalStocks = selectedStocks.value.length // This already includes the new stock
    const baseWeight = Math.floor(100 / totalStocks)
    const remainder = 100 % totalStocks
    
    // Update all stock weights
    selectedStocks.value.forEach((existingStock, index) => {
      // Add remainder to the last stock(s) to make up the difference
      const extraWeight = index >= totalStocks - remainder ? 1 : 0
      stockWeights.value[existingStock.symbol] = baseWeight + extraWeight
    })
    // Set weight for the newly selected stock (it's the last one)
    const extraWeight = remainder > 0 ? 1 : 0
    stockWeights.value[stock.symbol] = baseWeight + extraWeight
  }
}

const handleStockRemoved = (stock) => {
  // Remove weight when stock is removed
  if (stockWeights.value[stock.symbol]) {
    delete stockWeights.value[stock.symbol]
  }
  
  // Redistribute weights evenly among remaining stocks
  if (competition.value?.allow_weights && selectedStocks.value.length > 0) {
    const remainingStocks = selectedStocks.value.filter(s => s.symbol !== stock.symbol)
    if (remainingStocks.length > 0) {
      const baseWeight = Math.floor(100 / remainingStocks.length)
      const remainder = 100 % remainingStocks.length
      
      remainingStocks.forEach((remainingStock, index) => {
        // Add remainder to the last stock(s) to make up the difference
        const extraWeight = index >= remainingStocks.length - remainder ? 1 : 0
        stockWeights.value[remainingStock.symbol] = baseWeight + extraWeight
      })
    }
  }
}

const generateDisplayName = () => {
  const adjectives = ['Swift', 'Bold', 'Clever', 'Sharp', 'Bright', 'Quick', 'Smart', 'Wise', 'Fast', 'Bright']
  const nouns = ['Trader', 'Investor', 'Jockey', 'Player', 'Master', 'Pro', 'Guru', 'Whiz', 'Star', 'Champ']
  
  const randomAdjective = adjectives[Math.floor(Math.random() * adjectives.length)]
  const randomNoun = nouns[Math.floor(Math.random() * nouns.length)]
  const randomNumber = Math.floor(Math.random() * 999) + 1
  
  form.displayName = `${randomAdjective}${randomNoun}${randomNumber}`
}

const useFullName = () => {
  if (authStore.isAuthenticated) {
    form.displayName = authStore.user?.full_name || ''
  } else if (form.fullName.trim()) {
    form.displayName = form.fullName
  }
}

const handleWeightsUpdate = (newWeights) => {
  stockWeights.value = newWeights
}

const handleSubmit = async () => {
  loading.value = true
  error.value = ''
  success.value = false

  try {
    const stockPicks = selectedStocks.value.map(stock => ({
      symbol: stock.symbol,
      weight: competition.value.allow_weights ? 
        (parseFloat(stockWeights.value[stock.symbol]) / 100) : null
    }))

    // Use authenticated user data or form data
    const userData = authStore.isAuthenticated ? {
      name: authStore.user.full_name,
      email: authStore.user.email
    } : {
      name: form.fullName,
      email: form.email || null
    }

    const entryData = {
      user: userData,
      display_name: form.displayName,
      stock_picks: stockPicks
    }

    const response = await competitionStore.joinCompetition(competitionId, entryData)
    joinResponse.value = response
    success.value = true
    
    // Only refresh leaderboard and redirect for auto-approved requests
    if (response.status !== 'pending_approval') {
      // Refresh leaderboard data to ensure it's up-to-date
      try {
        await competitionStore.fetchLeaderboard(competitionId)
      } catch (refreshError) {
        console.warn('Failed to refresh leaderboard:', refreshError)
      }
      
      // Reset form
      Object.assign(form, {
        fullName: '',
        email: '',
        displayName: ''
      })
      selectedStocks.value = []
      stockWeights.value = {}
      
      // Redirect to competition details page after a short delay
      setTimeout(() => {
        router.push(`/competition/${competitionId}`)
      }, 1500)
    } else {
      // Reset form for pending requests
      Object.assign(form, {
        fullName: '',
        email: '',
        displayName: ''
      })
      selectedStocks.value = []
      stockWeights.value = {}
    }
  } catch (err) {
    error.value = err.message || 'Failed to join competition'
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  if (competitionId) {
    try {
      competition.value = await competitionStore.fetchCompetition(competitionId)
      
      // Pre-fill form for authenticated users
      if (authStore.isAuthenticated) {
        form.fullName = authStore.user?.full_name || ''
        form.email = authStore.user?.email || ''
        form.displayName = authStore.user?.full_name || ''
      }
      
      // Generate a default display name if not authenticated or no user name
      if (!authStore.isAuthenticated || !authStore.user?.full_name) {
        generateDisplayName()
      }
    } catch (err) {
      error.value = 'Failed to load competition details'
    }
  } else {
    error.value = 'No competition ID provided'
  }
})
</script> 